import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { pTicket, pTicketMap } from '../interfaces/pTicket';
import { PDescriptionService } from './p-description.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openSnackBar } from '../helpers/genericFunctions';
import { differences } from '../interfaces/loggingDifferences';
import { WebAddresses } from '../app.config';
import { ApiResponse } from '../interfaces/api-response';
import { pDescription } from '../interfaces/pDescription';
import { globalUser } from '../helpers/globalFields';
/**
 * interacts with pTicket API endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class PTicketService {
  /**
   * constructor
   *
   * @param {HttpClient} http The HttpClient that GET, POST, PUT, DELETE on the api
   * @param {PDescriptionService} pDescriptionService Service that interacts with pDescriptions
   * @param {MatSnackBar} snackBar Overlayed information bar to display errors and messages upon API calls
   */
  constructor(
    private http: HttpClient,
    private pDescriptionService: PDescriptionService,
    private snackBar: MatSnackBar,
  ) {}

  /**
   * allows easy switching between production and development webserver addresses for api
   */
  private baseUrl = WebAddresses.tCCTicketServer;

  /**
   * event emitter that triggers every time when some pTicket data is changed in the database. Used to reload the frontend view with changed data.
   */
  @Output() pTicketChanged = new EventEmitter();
  /**
   * message that is autmatically added to the pDescriptions of a ticket upon change of ticket information
   */
  message = <pDescription>{};

  /**
   * getAll is used to retrieve a subset of filtered pTickets from the API
   *
   * @param {HttpParams} filter Filter parameters for api call
   * @returns list of all pTickets that match the filter
   */
  getAll(filter: HttpParams = new HttpParams()): Observable<pTicket[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/pticket/findall`, {
        headers: globalUser.getHeaders(),
        params: filter,
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching pTickets:', error);

          return of([]);
          return throwError(() => new Error('Failed to fetch pTickets'));
        }),
      );
  }

  /**
   * gets a single pTicket by id
   *
   * @param {number} id pTicket id of singular ticket
   * @returns singular pTicket by id
   */
  getTicket(id: number): Observable<pTicket> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/pticket/findone/${id}`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching pTicket:', error);
          return throwError(() => new Error('Failed to fetch pTicket'));
        }),
      );
  }

  /**
   * updates pTicket by id
   *
   * @param id pTicket id of to be updated pTicket
   * @param data JSON containing the data, that should be updated
   * @returns updated pTicket
   */
  update(id: number, data: any): Observable<pTicket> {
    return this.http
      .put<ApiResponse>(`${this.baseUrl}/api/pticket/update/${id}`, data, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error updating pTicket:', error);
          return throwError(() => new Error('Failed to update pTicket'));
        }),
      );
  }

  /**
   * move ticket to new org by id
   *
   */
  move(id: number, data: any): Observable<pTicket> {
    return this.http
      .put<ApiResponse>(`${this.baseUrl}/api/pticket/move/${id}`, data, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error updating pTicket:', error);
          return throwError(() => new Error('Failed to update pTicket'));
        }),
      );
  }

  /**
   * create new pTicket
   *
   * @param data initial data with which the pTicket is created
   * @returns newly created pTicket
   */
  create(data: any): Observable<pTicket> {
    this.pTicketChanged.emit();
    return this.http
      .post<ApiResponse>(`${this.baseUrl}/api/pticket/create`, data, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            //openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error creating pTicket:', error);
          return throwError(() => new Error('Failed to create pTicket'));
        }),
      );
  }

  /**
   * upon update of pTicket a message is added to the pDescriptions of the ticket
   * to reflect and document these changes
   *
   * @param id pTicket id
   * @param data data that has been changed
   * @param differences differences between the already existing pTicket and the newly added changes
   * @param success_message message that is displayed in snackBar upon successfull change of the pTicket
   * @param error_message message that is displayed in snackBar if an error occurs during the update process
   */
  updatePTicket(
    id: any,
    data: any,
    differences: Array<differences>,
    success_message: string,
    error_message: string,
  ): Observable<any> {
    this.message.pticket_id = id;
    this.message.pde_type = 'ChatBot';
    this.message.pde_description = 'Info changed \n';
    for (let index = 0; index < differences.length; index++) {
      const element = differences[index];
      this.message.pde_description +=
        element.key + ':' + element.value1 + ' => ' + element.value2 + '\n';
    }
    return this.pDescriptionService.create(this.message).pipe(
      switchMap(() =>
        this.update(id, data).pipe(
          tap(() => {
            openSnackBar(success_message, 2000, this.snackBar);
            this.pTicketChanged.emit();
          }),
          catchError((err) => {
            openSnackBar(error_message + err, 10000, this.snackBar);
            return throwError(() => err);
          }),
        ),
      ),
      catchError((err) => {
        openSnackBar(error_message + err.message, 10000, this.snackBar);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Set a pTicket as inactive by id.
   *
   * @param id pTicket id of the ticket to set inactive
   * @returns updated pTicket
   */
  setInactive(id: number): Observable<pTicket> {
    return this.http
      .put<ApiResponse>(
        `${this.baseUrl}/api/pticket/setinactive/${id}`,
        {},
        {
          headers: globalUser.getHeaders(),
        },
      )
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error setting pTicket inactive:', error);
          return throwError(() => new Error('Failed to set pTicket inactive'));
        }),
      );
  }

  getForMap(): Observable<Record<string, number>> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/pticket/findformap`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? {}),
        catchError((error) => {
          console.error('Error fetching pTickets:', error);

          return of({});
        }),
      );
  }
}
